// Settings Hub layout. Renders PageShell + breadcrumb + header, then a
// two-column body: a vertical section sidebar on the left and the routed
// section content (<Outlet />) on the right. The sidebar carries a
// "Find a setting…" trigger that opens a ⌘K command palette for fuzzy search
// and keyboard navigation across every section.
//
// Replaces the original horizontal tab strip (LT-311). The active section
// lives in the URL (/settings/:slug) so deep links and the back button work;
// the last four opened sections persist in localStorage for the palette's
// "Recent" list. Legacy SettingsPage `?tab=...` URLs still redirect to the
// matching sub-route on mount so bookmarked links survive the cutover.
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Lock, Search, Settings as SettingsIcon } from 'lucide-react';
import clsx from 'clsx';
import PageShell from '../../components/PageShell';
import { Button } from '../../ui';
import { useAuth } from '../../context/AuthContext';
import { useInboundEligibility, useSmsEligibility } from '../../utils/entitlements';
import { SETTINGS_SECTIONS, SettingsSection, sectionPath } from './settingsSections';
import SettingsCommandPalette from './SettingsCommandPalette';

const RECENTS_KEY = 'settings.recents';
const RECENTS_LIMIT = 4;
const DEFAULT_RECENTS = ['team', 'billing', 'calling'];

function readRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return DEFAULT_RECENTS;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) {
      return (parsed as string[]).slice(0, RECENTS_LIMIT);
    }
  } catch {
    // corrupt or unavailable storage — fall through to the default seed
  }
  return DEFAULT_RECENTS;
}

function persistRecents(ids: string[]): void {
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(ids));
  } catch {
    // storage unavailable — recents degrade to in-memory only
  }
}

// Legacy ?tab= values → new sub-route slug. Preserves bookmarks from the
// pre-LT-311 `/settings?tab=<id>` pattern.
const LEGACY_QUERY_TAB_TO_SLUG: Record<string, string> = {
  account: 'account',
  members: 'team',
  email: 'senders',
  'email-admin': 'email-deliverability',
  pipelines: 'pipelines',
};

// First path segment under /settings — '' for the index (Overview).
function activeSlugFromPath(pathname: string): string {
  return pathname.replace(/^\/settings\/?/, '').split('/')[0] || '';
}

const SettingsHubLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { canSms } = useSmsEligibility(user);
  const { canInbound } = useInboundEligibility(user);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [recents, setRecents] = useState<string[]>(readRecents);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  // Back-compat: `/settings?tab=members` → `/settings/team`. Runs on mount and
  // whenever the query string changes; only fires at the hub root.
  useEffect(() => {
    if (location.pathname !== '/settings') return;
    const params = new URLSearchParams(location.search);
    const legacy = params.get('tab');
    if (!legacy) return;
    const slug = LEGACY_QUERY_TAB_TO_SLUG[legacy];
    if (slug) navigate(`/settings/${slug}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // ⌘K / Ctrl+K toggles the palette; Esc closes it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      } else if (e.key === 'Escape') {
        setPaletteOpen((open) => (open ? false : open));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Restore focus to the search trigger whenever the palette closes.
  useEffect(() => {
    if (wasOpenRef.current && !paletteOpen) {
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }
    wasOpenRef.current = paletteOpen;
  }, [paletteOpen]);

  // Sections visible to this user — hidden by entitlement / admin flag so
  // non-eligible users don't see dead links. Route-level guards still enforce
  // access on direct URL entry.
  const visibleSections = SETTINGS_SECTIONS.filter((s) => {
    if (s.adminOnly && !isAdmin()) return false;
    if (s.requires === 'sms' && !canSms) return false;
    if (s.requires === 'inbound' && !canInbound) return false;
    return true;
  });

  const activeSlug = activeSlugFromPath(location.pathname);
  const activeSection =
    SETTINGS_SECTIONS.find((s) => s.slug === activeSlug) ?? SETTINGS_SECTIONS[0];

  // Track the last four opened sections for the palette's "Recent" list.
  useEffect(() => {
    setRecents((prev) => {
      const next = [
        activeSection.id,
        ...prev.filter((id) => id !== activeSection.id),
      ].slice(0, RECENTS_LIMIT);
      persistRecents(next);
      return next;
    });
  }, [activeSection.id]);

  const handleSelect = (section: SettingsSection) => {
    setPaletteOpen(false);
    navigate(sectionPath(section));
  };

  return (
    <PageShell>
      {/* Breadcrumb is rendered by AuthenticatedLayout — no second copy here. */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-1.5 rounded-ds-lg bg-accent shadow-ds-glow-accent">
            <SettingsIcon className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-50">Settings</h1>
        </div>
        <p className="text-sm text-slate-400">
          Workspace, team, billing, and sequence infrastructure.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-5">
        <nav
          aria-label="Settings sections"
          className="lg:sticky lg:top-6 self-start flex flex-col gap-2.5"
        >
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2.5 w-full bg-surface-1 border border-white/[0.08] rounded-ds-lg px-2.5 py-2 text-[13px] text-slate-400 transition-colors hover:border-accent-border"
          >
            <Search className="w-3.5 h-3.5 shrink-0 text-slate-500" aria-hidden="true" />
            <span className="flex-1 text-left">Find a setting…</span>
            <kbd className="font-mono text-[10px] text-slate-500 bg-surface-2 border border-white/[0.08] rounded px-1.5 py-0.5">
              ⌘K
            </kbd>
          </button>

          <ul className="flex flex-col gap-px overflow-y-auto lg:max-h-[calc(100vh-260px)]">
            {visibleSections.map((section) => {
              const Icon = section.icon;
              return (
                <li key={section.id}>
                  <NavLink
                    to={sectionPath(section)}
                    end={section.slug === ''}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-2.5 px-2.5 py-1.5 rounded-ds-md text-[13px] border-l-2 transition-colors',
                        isActive
                          ? 'bg-surface-1 text-slate-50 border-accent font-medium'
                          : 'border-transparent text-slate-400 hover:text-slate-200',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          className={clsx(
                            'w-3.5 h-3.5 shrink-0',
                            isActive ? 'text-accent' : 'text-slate-500',
                          )}
                          aria-hidden="true"
                        />
                        <span className="truncate">{section.label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>

      {paletteOpen && (
        <SettingsCommandPalette
          sections={visibleSections}
          recents={recents}
          onSelect={handleSelect}
          onClose={() => setPaletteOpen(false)}
        />
      )}
    </PageShell>
  );
};

export default SettingsHubLayout;

// Re-exported helper so sub-routes can share a single "unauthorized" fallback
// rather than each one embedding the same redirect logic.
export const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  return isAdmin() ? <>{children}</> : <Navigate to="/settings" replace />;
};

export const RequireSms: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { canSms, loading: entitlementsLoading } = useSmsEligibility(user);
  if (loading || entitlementsLoading) return null;
  return canSms ? <>{children}</> : <Navigate to="/subscription" replace />;
};

// CCT-11 / CCT-6: when a workspace lacks the inbound entitlement, render a
// contextual upgrade gate in place of the config page instead of silently
// bouncing to Billing. The old `<Navigate to="/subscription">` (which itself
// forwards to /settings/billing) dropped the user on the generic plan list
// with no explanation — and made the /inbound-configuration legacy redirect
// look like it "forgot" where they wanted to go. Keeping them on the route
// and naming the missing entitlement fixes both reports.
const InboundUpgradeGate: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-xl rounded-ds-xl border border-white/[0.08] bg-surface-1 p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-ds-lg bg-accent-tint border border-accent-border">
          <Lock className="w-5 h-5 text-accent-text" aria-hidden="true" />
        </div>
        <h2 className="text-lg font-semibold text-slate-50">
          Inbound calling is a paid feature
        </h2>
      </div>
      <p className="text-sm text-slate-300">
        Inbound calling — answering incoming calls with your AI agent and
        configuring how they're routed — is available on the Growth and Pro
        plans. Your current plan doesn't include it yet.
      </p>
      <div className="mt-5">
        <Button intent="primary" onClick={() => navigate('/settings/billing')}>
          View plans
        </Button>
      </div>
    </div>
  );
};

export const RequireInbound: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { canInbound, loading: entitlementsLoading } = useInboundEligibility(user);
  if (loading || entitlementsLoading) return null;
  return canInbound ? <>{children}</> : <InboundUpgradeGate />;
};
